/*
 * Data Service
 * Features: Generic API request handler with route definitions, auth, and response processing
 * Logic: 
 * - Uses symlinked route definitions from API
 * - Pattern matching for parameterized routes (e.g., /api/data/chat/sessions/:id/messages)
 * - Automatic path parameter extraction and merging with payload
 * - Request/response validation with Zod schemas
 * - Automatic camelCase conversion of response data
 * Changes:
 * - Fixed route pattern matching to support parameterized routes with actual values
 * - Added matchPathPattern() function to extract path parameters from URLs
 * - Updated request() function to try exact match first, then pattern matching
 * - Added 401 retry flow to refresh token before redirecting to login
 * - Redirects to login on auth header failures when session is invalid
 * 
 * NOTE:
 * ./data-routes is a symlink to ../api/src/data-routes
 *
 * use the following command to create the symlink:
 * ln -s ../api/src/data-routes ./data-routes
 *
 * On windows, use mklink command:
 * mklink /D data-routes ..\api\src\data-routes
 */
import routes from './data-routes';
import { type DataRoutes, type Route } from './data-routes/types';
import { ensureRefresh, getAuthHeaders, handleResponse, redirectToLogin, RefreshFailedError } from './authService';
import { convertKeysToCamelCase } from '../utils/camelCase';
import { parseDataProperty } from '../utils/parseUtils';
import { z } from 'zod';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface ApiEndpointDefinition<TReq = any, TRes = any> {
  path: string;
  method: HttpMethod;
  schema?: {
    request?: z.Schema<TReq>;
    response: z.Schema<TRes>;
  };
}

// Convert routes to API endpoint definitions
const apiDefinitions = Object.entries(routes as DataRoutes).reduce((acc, [key, routeConfig]: [string, Route]) => {
  const [method, path] = key.split(' ');
  acc[key] = {
    path,
    method: method.toUpperCase() as HttpMethod,
    schema: routeConfig.schema ? {
      request: routeConfig.schema,
      response: z.any()
    } : undefined
  };
  return acc;
}, {} as Record<string, ApiEndpointDefinition>);

function replacePathParams(path: string, params: Record<string, string> = {}): string {
  return path.replace(/:([^/]+)/g, (_, param) => {
    const value = params[param];
    if (!value) throw new Error(`Missing required path parameter: ${param}`);
    return encodeURIComponent(String(value));
  });
}

function buildQueryString(query: Record<string, string | number | boolean | null> = {}): string {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(key, String(value));
    }
  });
  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

async function getAuthHeadersOrRedirect(): Promise<HeadersInit> {
  try {
    return await getAuthHeaders();
  } catch (error) {
    if (error instanceof RefreshFailedError && error.status === 401) {
      redirectToLogin();
    } else if (error instanceof Error && error.message.includes('Unauthorized')) {
      redirectToLogin();
    }
    throw error;
  }
}

async function makeRequest(methodPath: string, payload?: any): Promise<any> {
  const endpoint = apiDefinitions[methodPath];
  if (!endpoint) {
    throw new Error(`No route found: ${methodPath}`);
  }

  const isGetOrDelete = ['GET', 'DELETE'].includes(endpoint.method);
  let pathParams: Record<string, string> = {};
  let queryParams: Record<string, string | number | boolean | null> = {};

  // Extract path parameters and query parameters from payload
  if (payload) {
    const payloadObj = payload as Record<string, any>;
    const extractedPathParams: Record<string, string> = {};

    // First find all path parameters in the endpoint path
    const pathParamMatches = endpoint.path.match(/:([^/]+)/g) || [];
    const pathParamNames = pathParamMatches.map(param => param.substring(1));

    Object.entries(payloadObj).forEach(([key, value]) => {
      if (pathParamNames.includes(key)) {
        extractedPathParams[key] = String(value);
      } else if (isGetOrDelete && value !== undefined && value !== null) {
        // Only add to query params for GET/DELETE if value is defined
        queryParams[key] = value;
      }
    });

    pathParams = extractedPathParams;
  }

  const path = replacePathParams(endpoint.path, pathParams);
  const queryString = isGetOrDelete ? buildQueryString(queryParams) : '';
  const url = `${path}${queryString}`;

  const headers = await getAuthHeadersOrRedirect();
  const init: RequestInit = {
    method: endpoint.method,
    headers: {
      ...headers,
      'Content-Type': 'application/json'
    }
  };

  if (!isGetOrDelete && payload) {
    // For POST/PUT/PATCH, put all non-path params in the body
    const bodyData = { ...payload };
    // Remove path params from body
    const pathParamMatches = endpoint.path.match(/:([^/]+)/g) || [];
    const pathParamNames = pathParamMatches.map(param => param.substring(1));
    pathParamNames.forEach(param => delete bodyData[param]);
    init.body = JSON.stringify(bodyData);
  }

  let response = await fetch(url, init);

  if (response.status === 401) {
    try {
      await ensureRefresh();
    } catch (error) {
      if (error instanceof RefreshFailedError && error.status === 401) {
        redirectToLogin();
      }
      throw error;
    }

    const retryHeaders = await getAuthHeadersOrRedirect();
    const retryInit: RequestInit = {
      ...init,
      headers: {
        ...retryHeaders,
        'Content-Type': 'application/json'
      }
    };
    response = await fetch(url, retryInit);

    if (response.status === 401) {
      redirectToLogin();
      throw new Error('Unauthorized - Session expired');
    }
  }

  const data = await handleResponse(response);
  const parsedData = data ? parseDataProperty(data) : data;
  const processedData = parsedData ? convertKeysToCamelCase(parsedData) : parsedData;

  // Validate response if schema exists
  if (endpoint.schema?.response && processedData) {
    const validationResult = endpoint.schema.response.safeParse(processedData);
    if (!validationResult.success) {
      throw new Error(`Invalid response data: ${validationResult.error.message}`);
    }
  }

  return processedData;
}

/**
 * Check if a request path matches a route pattern
 * @param pattern The route pattern (e.g., "/api/data/chat/sessions/:id/messages")
 * @param path The request path (e.g., "/api/data/chat/sessions/6/messages")
 * @returns Object with extracted parameters or null if no match
 */
function matchPathPattern(pattern: string, path: string): Record<string, string> | null {
  const patternSegments = pattern.split('/');
  const pathSegments = path.split('/');

  if (patternSegments.length !== pathSegments.length) {
    return null;
  }

  const params: Record<string, string> = {};

  for (let i = 0; i < patternSegments.length; i++) {
    if (patternSegments[i].startsWith(':')) {
      const paramName = patternSegments[i].slice(1);
      params[paramName] = pathSegments[i];
    } else if (patternSegments[i] !== pathSegments[i]) {
      return null;
    }
  }

  return params;
}

/**
 * Generic request function for making API calls using route definitions
 * @param path The API path to request
 * @param payload Optional payload (used as body for POST/PUT/PATCH, or query/params for GET/DELETE)
 * @returns Promise resolving to the response data
 */
export async function request(fullPath: string, payload?: any): Promise<any> {
  const [reqMethod, reqPath] = fullPath.split(' ', 2);

  // First try exact match (case-insensitive comparison but preserve original key)
  let methodPath = Object.keys(apiDefinitions).find(key => {
    return key.toLowerCase() === fullPath.toLowerCase();
  });

  // If no exact match, try pattern matching
  if (!methodPath) {
    methodPath = Object.keys(apiDefinitions).find(key => {
      const [method, path] = key.split(' ', 2);
      if (method.toLowerCase() !== reqMethod.toLowerCase()) return false;

      const params = matchPathPattern(path, reqPath);
      return params !== null;
    });
  }

  if (!methodPath) {
    throw new Error(`No route found: ${fullPath}`);
  }

  // Extract path parameters from the actual path if pattern matching was used
  const [, routePattern] = methodPath.split(' ', 2);
  const pathParams = matchPathPattern(routePattern, reqPath);

  // Merge path params with payload
  const mergedPayload = pathParams ? { ...pathParams, ...payload } : payload;

  return makeRequest(methodPath, mergedPayload);
}
